import { Settings } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import type { CategoryFilterId, ControlMessageCategory } from '@/types/category';
import type { PredefinedMessage } from '@/types/predefined-messages';

import { ConfigurationModalCategoriesNavigation } from '@/components/configuration-modal/categories-navigation';
import { ContentPanel } from '@/components/configuration-modal/content-panel';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { ALL, CLOSED } from '@/constants';
import { useCategoryMutations } from '@/hooks/api/use-category-mutations';
import { useCategoriesQuery } from '@/hooks/api/use-category-queries';
import { usePredefinedMessageQueries } from '@/hooks/api/use-predefined-message-queries';
import { useCategoryListItems } from '@/lib/helpers/categories';
import { APP_ROUTES } from '@/lib/routes/app-routes';

export default function ConfigurationModal({ onTriggerClick }: { onTriggerClick?: () => void }) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const isArchive = pathname.includes(APP_ROUTES.archive);

  const {
    data: categoriesQueryData,
    isLoading: isCategoriesLoading,
    isError: isCategoriesError,
    refetch: refetchCategories,
  } = useCategoriesQuery(!isArchive);
  const { data: messagesQueryData } = usePredefinedMessageQueries();

  const [selectedCategoryId, setSelectedCategoryId] = useState<CategoryFilterId>(ALL);
  const [categoryForm, setCategoryForm] = useState<string>(CLOSED);

  const { deleteCategory, openEditCategory } = useCategoryMutations({
    selectedCategoryId,
    onSelectCategoryId: setSelectedCategoryId,
    categoryForm,
    setCategoryForm,
  });

  const categories: ControlMessageCategory[] = categoriesQueryData ?? [];
  const messages: PredefinedMessage[] = messagesQueryData ?? [];
  const categoryListItems = useCategoryListItems(messages, categories);

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          className="text-foreground h-12 w-full justify-start gap-3 px-5 font-medium tracking-normal"
          onClick={onTriggerClick}
        >
          <Settings className="text-foreground h-5 w-5" strokeWidth={2} />
          <span className="font-medium">{t('common:appMenu.configuration')}</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="align-center flex h-[90%] min-w-[80%] border-0 p-0">
        <DialogTitle className="sr-only">{t('common:appMenu.configuration')}</DialogTitle>
        <DialogDescription className="sr-only">
          {t('common:configuration.controlMessages.title')}
        </DialogDescription>
        <div className="bg-background flex h-full w-full rounded-lg">
          <ConfigurationModalCategoriesNavigation
            isCategoriesLoading={isCategoriesLoading}
            isCategoriesError={isCategoriesError}
            refetchCategories={refetchCategories}
            categories={categories}
            categoryListItems={categoryListItems}
            selectedCategoryId={selectedCategoryId}
            onSelectCategoryId={setSelectedCategoryId}
            onEditCategory={openEditCategory}
            onDeleteCategory={deleteCategory}
          />
          <ContentPanel
            key={`content-panel-${messages?.length}`}
            selectedCategoryId={selectedCategoryId}
            onSelectCategoryId={setSelectedCategoryId}
            messages={messages}
            categories={categories}
            categoryForm={categoryForm}
            setCategoryForm={setCategoryForm}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
